import * as Sentry from '@sentry/react';
import i18n from 'i18next';

import { getUserFromStorage, logout } from '@izimi/auth';
import {
  CommunicationsApi,
  ContactsApi,
  DocumentsApi,
  FetchService,
  HistoryLogApi,
  NotaryApi,
  NotificationsApi,
  PostalCodesApi,
  PublicApi,
  SharedApi,
  StudyApi,
  UserApi,
  VaultApi,
} from 'api'; // Don't replace with the izimi-api export; DocumentsApi is locally overridden.

import ConnectivePluginService from './connectivePlugin/ConnectivePluginService';
import config, { appSpec } from './config';

const fetchService = new FetchService({
  getAccessToken: () => Promise.resolve(getUserFromStorage()?.access_token),
  getLanguage: () => i18n.language,
  getAppSpec: () => appSpec,
  handleErrorResponse(res) {
    const isForbiddenOrUnauthorized = res.status === 401 || res.status === 403;
    if (res.status >= 400 || (res instanceof XMLHttpRequest && res.responseType === 'text')) {
      Sentry.withScope((scope) => {
        if (res instanceof Response) {
          scope.setTags({
            'x-trace-id': res.headers.get('x-trace-id'),
          });
        }
        scope.setExtra('data', res);
        scope.setTags({
          'http-status': res.status,
        });
        Sentry.captureException(new Error('api error'));
      });
    }

    // server side
    if (typeof window === 'undefined') {
      return Promise.reject(res);
    }

    if (isForbiddenOrUnauthorized) {
      logout();
      return Promise.reject(res);
    }
    if ('readyState' in res) {
      // this is the response of xmlhttprequests errors (upload document)
      try {
        return JSON.parse(res.response);
      } catch {
        return res.response;
      }
    }
    return Promise.reject(res);
  },
  logRequests: import.meta.env.DEV,
});

fetchService.setApiUrl(import.meta.env.DEV ? `/api` : `${config.apiUrl}/api`);

export const connectivePluginService = new ConnectivePluginService();

export const publicApi = new PublicApi(fetchService);
export const documentsApi = new DocumentsApi(fetchService);
export const userApi = new UserApi(fetchService);
export const postalCodesApi = new PostalCodesApi(fetchService);
export const contactsApi = new ContactsApi(fetchService);
export const notaryApi = new NotaryApi(fetchService);
export const vaultApi = new VaultApi(fetchService);
export const notificationsApi = new NotificationsApi(fetchService);
export const historyLogApi = new HistoryLogApi(fetchService);
export const sharedApi = new SharedApi(fetchService);
export const studyApi = new StudyApi(fetchService);
export const communicationsApi = new CommunicationsApi(fetchService);
